import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { Customer, CustomerFormData, Pagination } from "./customer";
import { customerService } from "./customerService";
import toast from "react-hot-toast";
import { USE_STUB_DATA, STUB_CUSTOMERS } from "../../config/stubData";
import { DEFAULT_PAGINATION } from "../../constants/pagination";

interface CustomerState {
  customers: Customer[];
  pagination: Pagination;
  selectedCustomer: Customer | null;
  loading: boolean;
  error: string | null;
}

const initialState: CustomerState = {
  customers: USE_STUB_DATA ? STUB_CUSTOMERS : [],
  pagination: {
    page: DEFAULT_PAGINATION.page,
    limit: DEFAULT_PAGINATION.limit,
    pages: 1,
    total: USE_STUB_DATA ? STUB_CUSTOMERS.length : 0,
  },
  selectedCustomer: null,
  loading: false,
  error: null,
};

// Using centralized stub data from config/stubData.ts

// Async thunks
// export const fetchCustomers = createAsyncThunk(
//   "customers/fetchCustomers",
//   // async (_, { rejectWithValue }) => {
//   //   try {
//   //     if (USE_STUB_DATA) {
//   //       return STUB_CUSTOMERS;
//   //     } else {
//   //       const response = await customerService.getCustomers();
//   //       if (response.success) {
//   //         // Transform API data to match local Customer interface
//   //         const transformedCustomers: Customer[] = response.data.customers.map(
//   //           (apiCustomer: any) => ({
//   //             id: apiCustomer.id,
//   //             name: apiCustomer.name,
//   //             code: apiCustomer.code || `C${apiCustomer.id}`,
//   //             email: apiCustomer.email,
//   //             phone: apiCustomer.phone,
//   //             address: apiCustomer.address || "",
//   //             city: apiCustomer.city || "",
//   //             state: apiCustomer.state || "",
//   //             zipCode: apiCustomer.postal_code || apiCustomer.zipCode || "",
//   //             contactPerson:
//   //               apiCustomer.contact_person || apiCustomer.contactPerson || "",
//   //             contactPhone:
//   //               apiCustomer.contact_phone || apiCustomer.contactPhone || "",
//   //             contactEmail:
//   //               apiCustomer.contact_email || apiCustomer.contactEmail || "",
//   //             status: apiCustomer.is_active || apiCustomer.status || true,
//   //             branchId: apiCustomer.branch_id || apiCustomer.branchId || "",
//   //             createdAt: apiCustomer.created_at || apiCustomer.createdAt,
//   //             updatedAt: apiCustomer.updated_at || apiCustomer.updatedAt,
//   //           })
//   //         );
//   //         return transformedCustomers;
//   //       } else {
//   //         return rejectWithValue("Failed to fetch customers");
//   //       }
//   //     }
//   //   } catch (error: any) {
//   //     return rejectWithValue(
//   //       error.response?.data?.message || "Failed to fetch customers"
//   //     );
//   //   }
//   // }
//   async (
//     { page = 1, limit = 10 }: { page?: number; limit?: number },
//     { rejectWithValue }
//   ) => {
//     try {
//       if (USE_STUB_DATA) {
//         return STUB_CUSTOMERS;
//       } else {
//         const response = await customerService.getCustomers({ page, limit });

//         if (response.success) {
//           const transformedCustomers: Customer[] = response.data.customers.map(
//             (apiCustomer: any) => ({
//               id: apiCustomer.id,
//               name: apiCustomer.name,
//               code: apiCustomer.code || `C${apiCustomer.id}`,
//               email: apiCustomer.email,
//               phone: apiCustomer.phone,
//               address: apiCustomer.address || "",
//               city: apiCustomer.city || "",
//               state: apiCustomer.state || "",
//               zipCode: apiCustomer.postal_code || apiCustomer.zipCode || "",
//               contactPerson:
//                 apiCustomer.contact_person || apiCustomer.contactPerson || "",
//               contactPhone:
//                 apiCustomer.contact_phone || apiCustomer.contactPhone || "",
//               contactEmail:
//                 apiCustomer.contact_email || apiCustomer.contactEmail || "",
//               status: apiCustomer.is_active || apiCustomer.status || true,
//               branchId: apiCustomer.branch_id || apiCustomer.branchId || "",
//               createdAt: apiCustomer.created_at || apiCustomer.createdAt,
//               updatedAt: apiCustomer.updated_at || apiCustomer.updatedAt,
//             })
//           );

//           return {
//             customers: transformedCustomers,
//             pagination: response.data.pagination,
//           };
//         } else {
//           return rejectWithValue("Failed to fetch customers");
//         }
//       }
//     } catch (error: any) {
//       return rejectWithValue(
//         error.response?.data?.message || "Failed to fetch customers"
//       );
//     }
//   }
// );

export const fetchCustomers = createAsyncThunk<
  { customers: Customer[]; pagination: Pagination },
  { page?: number; limit?: number; search?: string; status?: string; customer_type?: string },
  { rejectValue: string }
>("customers/fetchCustomers", async (params = {}, { rejectWithValue }) => {
  try {
    if (USE_STUB_DATA) {
      const { 
        page = DEFAULT_PAGINATION.page, 
        limit = DEFAULT_PAGINATION.limit, 
        search = "", 
        status = "", 
        customer_type = "" 
      } = params;
      
      // Apply filters to stub data
      let filteredCustomers = STUB_CUSTOMERS;
      
      if (search) {
        filteredCustomers = filteredCustomers.filter(customer => 
          customer.name.toLowerCase().includes(search.toLowerCase()) ||
          customer.email.toLowerCase().includes(search.toLowerCase()) ||
          customer.phone?.toLowerCase().includes(search.toLowerCase()) ||
          customer.code?.toLowerCase().includes(search.toLowerCase())
        );
      }
      
      if (status !== "") {
        const isActive = status === "active";
        filteredCustomers = filteredCustomers.filter(customer => customer.status === isActive);
      }
      
      if (customer_type) {
        filteredCustomers = filteredCustomers.filter(customer => customer.type === customer_type);
      }
      
      // Apply pagination
      const total = filteredCustomers.length;
      const pages = Math.ceil(total / limit);
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedCustomers = filteredCustomers.slice(startIndex, endIndex);
      
      return {
        customers: paginatedCustomers,
        pagination: {
          page,
          limit,
          pages,
          total,
        }
      };
    } else {
        const response = await customerService.getCustomers(params);
        if (response.success) {
          const transformedCustomers: Customer[] = response.data.customers.map((apiCustomer: any) => ({
            id: apiCustomer.id,
            name: apiCustomer.name,
            code: apiCustomer.code || `C${apiCustomer.id}`,
            email: apiCustomer.email,
            phone: apiCustomer.phone,
            whatsapp: apiCustomer.whatsapp || "",
            address: apiCustomer.address || "",
            city: apiCustomer.city || "",
            state: apiCustomer.state || "",
            zipCode: apiCustomer.postal_code || apiCustomer.zipCode || "",
            contactPerson: apiCustomer.contact_person || apiCustomer.contactPerson || "",
            contactPhone: apiCustomer.contact_phone || apiCustomer.contactPhone || "",
            contactEmail: apiCustomer.contact_email || apiCustomer.contactEmail || "",
            status: apiCustomer.is_active || apiCustomer.status || true,
            branchId: apiCustomer.branch_id || apiCustomer.branchId || "",
            type: apiCustomer.type || "individual",
            referenceBy: apiCustomer.reference_by || apiCustomer.referenceBy || "",
            createdAt: apiCustomer.created_at || apiCustomer.createdAt,
            updatedAt: apiCustomer.updated_at || apiCustomer.updatedAt,
          }));

          return {
            customers: transformedCustomers,
            pagination: response.data.pagination || {
              page: DEFAULT_PAGINATION.page,
              limit: DEFAULT_PAGINATION.limit,
              pages: 1,
              total: transformedCustomers.length,
            }
          };
        } else {
          return rejectWithValue("Failed to fetch customers");
        }
      }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch customers"
      );
    }
  }
);


export const fetchCustomerById = createAsyncThunk(
  "customers/fetchCustomerById",
  async (customerId: string, { rejectWithValue }) => {
    try {
        const response = await customerService.getCustomer(customerId);
        if (response.success) {
          const apiCustomer = response.data;
         
          console.log("customer from slice=====", apiCustomer);
          return apiCustomer;
          
        } else {
          return rejectWithValue("Failed to fetch customer");
        }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch customer"
      );
    }
  }
);

export const createCustomer = createAsyncThunk(
  "customers/createCustomer",
  async (customerData: CustomerFormData, { rejectWithValue }) => {
    try {
      if (USE_STUB_DATA) {
        const newCustomer: Customer = {
          ...customerData,
          id: Math.random().toString(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        return newCustomer;
      } else {
        const response = await customerService.createCustomer(customerData);
        if (response.success) {
          // console.log("response========>",response.data)
          // console.log("response .cusomer=======>",response.data.customer)
          // const apiCustomer = response.data;

          // console.log('API response after create:', apiCustomer)
          return response.data.customer;
        } else {
          // Extract error message from response, errorHandlingService already showed toast
          const errorMessage = response.error?.message || "Failed to create customer";
          return rejectWithValue(errorMessage);
        }
      }
    } catch (error: any) {
      console.error("Create customer error:", error);
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.message || 
                          error.message || 
                          "Failed to create customer";
      return rejectWithValue(errorMessage);
    }
  }
);

export const editCustomer = createAsyncThunk(
  "customers/editCustomer",
  async (
    { id, customerData }: { id: string; customerData: CustomerFormData },
    { rejectWithValue }
  ) => {
    try {
      if (USE_STUB_DATA) {
        const updatedCustomer: Customer = {
          ...customerData,
          id,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        return updatedCustomer;
      } else {
        const response = await customerService.updateCustomer(id, {
          name: customerData.name,
          email: customerData.email,
          phone: customerData.phone,
          whatsapp: customerData.whatsapp,
          code:customerData.code,
          address: customerData.address,
          city: customerData.city,
          state: customerData.state,
          zipCode: customerData.zipCode,
          contactPerson: customerData.contactPerson,
          contactPhone: customerData.contactPhone,
          contactEmail: customerData.contactEmail,
          referenceBy: customerData.referenceBy,
        });
        if (response.success) {
          const apiCustomer = response.data;
          // Transform API data to match local Customer interface
          const transformedCustomer: Customer = {
            id: apiCustomer.id,
            name: apiCustomer.name,
            code: apiCustomer.code || `C${apiCustomer.id}`,
            email: apiCustomer.email,
            phone: apiCustomer.phone,
            whatsapp: apiCustomer.whatsapp || "",
            address: apiCustomer.address || "",
            city: apiCustomer.city || "",
            state: apiCustomer.state || "",
            zipCode: apiCustomer.postal_code || apiCustomer.zipCode || "",
            contactPerson:
              apiCustomer.contact_person || apiCustomer.contactPerson || "",
            contactPhone:
              apiCustomer.contact_phone || apiCustomer.contactPhone || "",
            contactEmail:
              apiCustomer.contact_email || apiCustomer.contactEmail || "",
            status: apiCustomer.is_active || apiCustomer.status || true,
            branchId: apiCustomer.branch_id || apiCustomer.branchId || "",
            type: apiCustomer.type || "individual",
            referenceBy: apiCustomer.reference_by || apiCustomer.referenceBy || "",
            createdAt: apiCustomer.created_at || apiCustomer.createdAt,
            updatedAt: apiCustomer.updated_at || apiCustomer.updatedAt,
          };
          // return transformedCustomer;
          return response.data
        } else {
          return rejectWithValue("Failed to update customer");
        }
      }
    } catch (error: any) {
      console.error("Update customer error:", error);
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.message || 
                          error.message || 
                          "Failed to update customer";
      return rejectWithValue(errorMessage);
    }
  }
);

export const removeCustomer = createAsyncThunk(
  "customers/removeCustomer",
  async (id: string, { rejectWithValue }) => {
    try {
      if (USE_STUB_DATA) {
        return id;
      } else {
        // await customerService.deleteCustomer(id);
        // return id;
        const response = await customerService.deleteCustomer(id);
        if (response.success) {
          return id;
        } else {
          // Reject with error message from API
          return rejectWithValue(response.error?.message || "Failed to delete customer");
        }
      }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete customer"
      );
    }
  }
);

const customerSlice = createSlice({
  name: "customers",
  initialState,
  reducers: {
    setSelectedCustomer: (state, action: PayloadAction<Customer | null>) => {
      state.selectedCustomer = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setPagination: (state, action: PayloadAction<Partial<Pagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    // fetchCustomers
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // .addCase(fetchCustomers.fulfilled, (state, action) => {
      //   console.log('createCustomer.fulfilled payload:', action.payload)
      //   state.loading = false;
      //   state.customers = action.payload;
      // })
      // .addCase(fetchCustomers.fulfilled, (state, action) => {
      //   state.loading = false;
      //   if (action.payload.pagination.page > 1) {
      //     state.customers = [...state.customers, ...action.payload.customers];
      //   } else {
      //     state.customers = action.payload.customers;
      //   }
      //   state.pagination = action.payload.pagination;
      // })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = action.payload.customers;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Error toast is already handled by errorHandlingService in enhancedApiClient
      });

    // fetchCustomerById
    builder
      .addCase(fetchCustomerById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomerById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedCustomer = action.payload;
      })
      .addCase(fetchCustomerById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Error toast is already handled by errorHandlingService in enhancedApiClient
      });

    // createCustomer
    builder
      .addCase(createCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        console.log('------->createCustomer data ', action.payload)
        console.log("=====>action.paylod=> ", action.payload)
        console.log("*********action=> ", action)
        state.loading = false;
        state.customers.unshift(action.payload);
        toast.success("Customer created successfully");
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Error toast is already handled by errorHandlingService in enhancedApiClient
        // No need to show duplicate toast here
      });

    // editCustomer
    builder
      .addCase(editCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editCustomer.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.customers.findIndex(
          (c) => c.id === action.payload.id
        );
        if (index !== -1) {
          state.customers[index] = action.payload;
        }
        if (state.selectedCustomer?.id === action.payload.id) {
          state.selectedCustomer = action.payload;
        }
        toast.success("Customer updated successfully");
      })
      .addCase(editCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Error toast is already handled by errorHandlingService in enhancedApiClient
        // No need to show duplicate toast here
      });

    // removeCustomer
    builder
      .addCase(removeCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = state.customers.filter(
          (c) => c.id !== action.payload
        );
        if (state.selectedCustomer?.id === action.payload) {
          state.selectedCustomer = null;
        }
        toast.success("Customer deleted successfully");
      })
      .addCase(removeCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Error toast is already handled by errorHandlingService in enhancedApiClient
      });
  },
});

export const { setSelectedCustomer, clearError, setPagination } = customerSlice.actions;

// Selectors
export const selectCustomers = (state: any) => state.customers.customers;
export const selectCustomerLoading = (state: any) =>
  state.customers.loading;
export const selectCustomerError = (state: any) => state.customers.error;
export const selectSelectedCustomer = (state: any) =>
  state.customers.selectedCustomer;
export const selectCustomerPagination = (state: any) => state.customers.pagination;


export default customerSlice.reducer;
